import { useBlockAllKeyboardShortcuts, useEffectOnce } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { useIsTelekomProject } from '@core/logic/hooks/useIsTelekomProject';
import { useGetSplineElementOrderNumbersQuery } from '@core/redux/query';
import { Path, XOutline } from '@deepup/icons';
import { elementThunks } from '@elements/redux';
import { type SplineElement } from '@elements/types';
import { Dialog, DialogTitle, IconButton } from '@mui/material';
import { setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { OrderNumberType } from '@projects/types/orderNumbers';
import { useState } from 'react';
import { DIALOG_WIDTH, ElementDialogProps } from './ElementDialog';
import { ElementSMAs } from './ElementSMAs';
import { SplineElementProperties } from './SplineElementProperties';

export const SplineElementDialog = ({
  onDelete,
  onClose,
  elementToUpdate,
}: ElementDialogProps<SplineElement>) => {
  const dispatch = useAppDispatch();
  const { isTelekomProject } = useIsTelekomProject();
  const [editStep, setEditStep] = useState<'PROPERTIES' | 'SMAS'>('PROPERTIES');
  const [editedSplineElement, setEditedSplineElement] = useState<SplineElement>();
  const { data: splineElementOrderNumbers, refetch: refetchSplineElementOrderNumbers } =
    useGetSplineElementOrderNumbersQuery(editedSplineElement?.id);

  useBlockAllKeyboardShortcuts();
  useEffectOnce(() => {
    setEditMode(EditMode.Element);
  });

  const onNext = async (editedSplineElement: SplineElement) => {
    if (isTelekomProject) {
      setEditStep('SMAS');
      setEditedSplineElement(editedSplineElement);
    } else {
      onClose();
    }
  };

  const onSubmitSmas = async (smas: string[]) => {
    if (!editedSplineElement) {
      onClose();
      return;
    }

    const elementWithSmas: SplineElement = {
      ...editedSplineElement,
      orderNumbers: smas.map((sma) => ({
        value: sma,
        type: OrderNumberType.SMA,
      })),
    };

    await dispatch(
      elementThunks.patch({
        path: ['projects', editedSplineElement.projectId, 'splineElements', editedSplineElement.id],
        body: elementWithSmas,
      }),
    );

    refetchSplineElementOrderNumbers();

    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        elevation: 4,
        sx: editStep === 'PROPERTIES' ? { width: DIALOG_WIDTH } : {},
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle variant="h6" gap={2} display="flex" alignItems="center">
        <Path fill="currentColor" fontSize={24} />
        {elementToUpdate ? 'Edit Spline Element' : 'Add Spline Element'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
          })}
          size="large"
        >
          <XOutline fontSize={24} />
        </IconButton>
      </DialogTitle>
      {editStep === 'PROPERTIES' && (
        <SplineElementProperties
          onClose={onClose}
          onDelete={onDelete}
          elementToUpdate={elementToUpdate}
          onNext={onNext}
        />
      )}
      {editStep === 'SMAS' && (
        <ElementSMAs
          selectedSmas={splineElementOrderNumbers?.map((on) => on.value) ?? []}
          onSubmitSmas={onSubmitSmas}
          onClose={onClose}
          onDelete={onDelete}
          submitButtonLabel="Update Spline Element"
        />
      )}
    </Dialog>
  );
};
